import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Flex,
  Badge,
  Table,
  Tbody,
  Tr,
  Td,
  Skeleton,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { Product } from "../types";
import Breadcrumb from "../components/Breadcrumb";
import Carousel from "../components/Carousel";
import { ShutterMark } from "../components/ProductCard";
import InquiryModal from "../components/InquiryModal";
import { slugify } from "../utils/slugify";

const availabilityColor: Record<string, string> = {
  "in-stock": "green",
  "out-of-stock": "red",
  "pre-order": "orange",
};

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    let cancelled = false;
    const fetchProduct = async () => {
      if (!productId) return;
      setIsLoading(true);
      try {
        const snap = await getDoc(doc(db, "products", productId));
        if (!cancelled) {
          setProduct(snap.exists() ? ({ id: snap.id, ...snap.data() } as Product) : null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (isLoading) {
    return (
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={10}>
        <Skeleton h="400px" borderRadius="10px" />
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={20}>
        <Text color="rs.textMuted">Product not found.</Text>
      </Container>
    );
  }

  const images = product.images || [];
  const categorySlug = product.categorySlug ?? slugify(product.category);

  return (
    <Box as="main" bg="rs.bg" minH="100vh">
      <Helmet>
        <title>{product.name} | Red Shutter Camera Store</title>
        <meta name="description" content={product.description} />
      </Helmet>
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={10}>
        <Breadcrumb
          crumbs={[
            { label: product.category, to: `/category/${categorySlug}` },
            { label: product.name },
          ]}
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          <Box>
            {images.length > 0 ? (
              <Carousel showDots={images.length > 1} showArrows={images.length > 1}>
                {images.map((img) => (
                  <Box
                    key={img.publicId}
                    flex="0 0 100%"
                    minW={0}
                    h={{ base: "320px", md: "440px" }}
                    bg="rs.imageWell"
                    border="1px solid"
                    borderColor="rs.border"
                    borderRadius="10px"
                    backgroundImage={`url(${img.url})`}
                    backgroundSize="contain"
                    backgroundRepeat="no-repeat"
                    backgroundPosition="center"
                  />
                ))}
              </Carousel>
            ) : (
              <Flex
                h="440px"
                align="center"
                justify="center"
                bg="rs.imageWell"
                border="1px solid"
                borderColor="rs.border"
                borderRadius="10px"
              >
                <Box maxW="200px">
                  <ShutterMark />
                </Box>
              </Flex>
            )}
          </Box>

          <VStack align="stretch" spacing={5}>
            <Text
              fontSize="10px"
              fontWeight="600"
              letterSpacing="0.12em"
              textTransform="uppercase"
              color="rs.textDim"
            >
              {product.category}
            </Text>
            <Heading
              fontFamily="'DM Serif Display', serif"
              fontSize={{ base: "28px", md: "34px" }}
              fontWeight="400"
              color="rs.text"
            >
              {product.name}
            </Heading>

            {product.availability && (
              <Badge
                colorScheme={availabilityColor[product.availability] ?? "gray"}
                w="fit-content"
                textTransform="capitalize"
              >
                {product.availability.replace("-", " ")}
              </Badge>
            )}

            <Text fontSize="22px" fontWeight="500" color="rs.text">
              {product.price.toLocaleString("en-LK")} LKR
            </Text>

            <Text fontSize="14px" color="rs.textMuted" lineHeight="1.75" whiteSpace="pre-wrap">
              {product.description}
            </Text>

            {product.specs && Object.keys(product.specs).length > 0 && (
              <Box border="1px solid" borderColor="rs.border" borderRadius="8px" overflow="hidden">
                <Table size="sm">
                  <Tbody>
                    {Object.entries(product.specs).map(([key, value]) => (
                      <Tr key={key}>
                        <Td
                          fontSize="12px"
                          color="rs.textDim"
                          textTransform="capitalize"
                          fontWeight="600"
                        >
                          {key}
                        </Td>
                        <Td fontSize="12px" color="rs.text">
                          {value}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            <Flex
              as="button"
              onClick={onOpen}
              bg="rs.red"
              color="white"
              fontSize="13px"
              fontWeight="500"
              letterSpacing="0.03em"
              px={7}
              py="12px"
              borderRadius="6px"
              justify="center"
              transition="background 0.15s"
              _hover={{ bg: "rs.redHover" }}
              w="fit-content"
            >
              Enquire about this item
            </Flex>

            <RouterLink to={`/category/${categorySlug}`}>
              <Text fontSize="12.5px" color="rs.textDim" _hover={{ color: "rs.text" }}>
                ← Back to {product.category}
              </Text>
            </RouterLink>
          </VStack>
        </SimpleGrid>
      </Container>

      <InquiryModal product={product} isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default ProductDetail;
